import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { translate } from "react-i18next";
import { withStyles } from "@material-ui/core/styles";
import { showSnackbar } from "@Actions/snackbarActions";
import styles from "./styles";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Switch,
  TextField,
} from "@material-ui/core";
import userService from "@Services/User";
import TagInput from "@Components/TagInput";
import DialogPopup from "@Components/DialogPopup";
import { getWatermarkProviders } from "@Actions/globalActions";

class WatermarkProvidersSettings extends Component {
  state = {
    providerDetailsOpen: null, // ID of the currently open provider details, null if none is open
    apiKeysByProvider: {},
    tagsByProvider: () => {
      const initialTagsByProvider = {};
      if (this.props.globalReducer.watermarkProviders) {
        this.props.globalReducer.watermarkProviders.forEach((provider) => {
          initialTagsByProvider[provider.id] = provider.tags || [];
        });
      }
      return initialTagsByProvider;
    },
    showUnconfirmedFindingsByProvider: () => {
      const initialShowUnconfirmedFindingsByProvider = {};
      if (this.props.globalReducer.watermarkProviders) {
        this.props.globalReducer.watermarkProviders.forEach((provider) => {
          initialShowUnconfirmedFindingsByProvider[provider.id] =
            provider.showUnconfirmedFindings || false;
        });
      }
      return initialShowUnconfirmedFindingsByProvider;
    }, // New state to track the "Show Unconfirmed Findings" setting for each provider
    activeStatusByProvider: () => {
      const initialActiveStatusByProvider = {};
      if (this.props.globalReducer.watermarkProviders) {
        this.props.globalReducer.watermarkProviders.forEach((provider) => {
          initialActiveStatusByProvider[provider.id] = provider.active || false;
        });
      }
      return initialActiveStatusByProvider;
    }, // New state to track the active status for each provider
    isSaving: false, // State to track if settings are currently being saved
    open: false, // State to control the confirmation dialog
  };

  //needed to update when the reducer has finished loading
  componentDidUpdate(prevProps) {
    if (
      prevProps.globalReducer &&
      this.props.globalReducer.watermarkProviders &&
      // check if any provider IDs have changed or if the order of providers has changed, to avoid unnecessary state updates
      prevProps.globalReducer.watermarkProviders
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((p) => p.id)
        .join(",") !==
        this.props.globalReducer.watermarkProviders
          .sort((a, b) => a.id.localeCompare(b.id))
          .map((p) => p.id)
          .join(",")
    ) {
      const newTagsByProvider = {};
      this.props.globalReducer.watermarkProviders.forEach((provider) => {
        newTagsByProvider[provider.id] = provider.tags || [];
      });
      this.setState({ tagsByProvider: newTagsByProvider });

      const newShowUnconfirmedFindingsByProvider = {};
      this.props.globalReducer.watermarkProviders.forEach((provider) => {
        newShowUnconfirmedFindingsByProvider[provider.id] =
          provider.showUnconfirmedFindings || false;
      });
      this.setState({
        showUnconfirmedFindingsByProvider: newShowUnconfirmedFindingsByProvider,
      });

      const newActiveStatusByProvider = {};
      this.props.globalReducer.watermarkProviders.forEach((provider) => {
        newActiveStatusByProvider[provider.id] = provider.active || false;
      });
      this.setState({
        activeStatusByProvider: newActiveStatusByProvider,
      });
    }
  }

  componentDidMount() {
    userService.getWatermarkProviders().then((response) => {
      this.props.actionGetWatermarkProviders(response);
    });
  }

  handleProviderToggle = (providerId, event) => {
    event.stopPropagation(); // Prevent the click event from propagating to the Paper component
    var setToFalse = false;
    this.setState((prevState) => {
      if (
        !prevState.activeStatusByProvider[providerId] === false && // if set to false
        providerId.startsWith("__new_provider_id_for_") === false // and was active at load
      ) {
        setToFalse = true;
      }

      if (setToFalse === true) {
        // ask first if they are sure, because this will delete all related watermark settings
        this.setState({ open: true });
      } else
        return {
          activeStatusByProvider: {
            ...prevState.activeStatusByProvider,
            [providerId]: !prevState.activeStatusByProvider[providerId],
          },
        };
    });

    if (
      !this.state.activeStatusByProvider[providerId] === false && // if set to false
      providerId.startsWith("__new_provider_id_for_") === false // and was active at load
    ) {
      event.preventDefault();
      return false;
    }
  };

  handleKeyChange = (providerId, value) => {
    this.setState((prevState) => ({
      apiKeysByProvider: {
        ...prevState.apiKeysByProvider,
        [providerId]: value,
      },
    }));
  };

  toggleDetails = (providerId, event) => {
    if (event.target.type === "checkbox") {
      return; // Don't toggle details if the click originated from the switch
    }
    this.setState((prevState) => ({
      providerDetailsOpen:
        prevState.providerDetailsOpen === providerId ? null : providerId,
    }));
  };

  tagsChanged = (providerId, action, value) => {
    if (action === "select-option") {
      this.setState((prevState) => {
        const currentTags = prevState.tagsByProvider[providerId] || [];
        return {
          tagsByProvider: {
            ...prevState.tagsByProvider,
            [providerId]: [...currentTags, value],
          },
        };
      });
    } else if (action === "remove-option") {
      this.setState((prevState) => {
        const currentTags = prevState.tagsByProvider[providerId] || [];
        return {
          tagsByProvider: {
            ...prevState.tagsByProvider,
            [providerId]: currentTags.filter((tag) => tag.id !== value.id),
          },
        };
      });
    }
  };

  handleFindingsSettingChange = (providerId, event) => {
    const newValue = event.target.checked;
    this.setState((prevState) => {
      return {
        showUnconfirmedFindingsByProvider: {
          ...prevState.showUnconfirmedFindingsByProvider,
          [providerId]: newValue,
        },
      };
    });
  };

  saveChanges = (provider) => {
    if (this.state.activeStatusByProvider[provider.id] !== true) {
      return;
    }
    this.setState({ isSaving: true });
    const settings = {
      active: this.state.activeStatusByProvider[provider.id] || false,
      apiKey:
        this.state.apiKeysByProvider[provider.id] === "********"
          ? ""
          : this.state.apiKeysByProvider[provider.id] || "",
      tags: this.state.tagsByProvider[provider.id].map((x) => x.id) || [],
      showUnconfirmedFindings:
        this.state.showUnconfirmedFindingsByProvider[provider.id] ||
        this.state.showUnconfirmedFindingsByProvider[provider.id] ||
        false,
    };

    userService.saveWatermarkProviderSettings(provider.name, settings).then(
      (response) => {
        this.props.actionShowSnackbar({
          message: this.props.t("_provider_settings_updated"),
          variant: "success",
        });
        this.setState({ isSaving: false });

        userService.getWatermarkProviders().then((response) => {
          this.props.actionGetWatermarkProviders(response);
        });
      },
      (error) => {
        this.props.actionShowSnackbar({
          message: this.props.t("_error_updating_provider_settings"),
          variant: "error",
        });
        this.setState({ isSaving: false });
      },
    );
  };

  handleCloseResetPopup = () => {
    this.setState({ open: false });
  };

  actionClickedResetPopup = () => {
    const providerId = this.state.providerDetailsOpen;
    const provider = this.props.globalReducer.watermarkProviders.find(
      (p) => p.id === providerId,
    );
    if (provider) {
      const settings = {
        active: false,
        apiKey: "",
        tags: [],
        showUnconfirmedFindings: false,
      };

      userService.saveWatermarkProviderSettings(provider.name, settings).then(
        (response) => {
          this.props.actionShowSnackbar({
            message: this.props.t("_provider_settings_deleted"),
            variant: "success",
          });
          this.setState({ open: false });

          // Optionally, you can also refresh the provider list here to get the updated settings from the server
          this.setState({ isSaving: false });
          userService.getWatermarkProviders().then((response) => {
            this.props.actionGetWatermarkProviders(response);
          });
        },
        (error) => {
          this.props.actionShowSnackbar({
            message: this.props.t("_error_updating_provider_settings"),
            variant: "error",
          });
          this.setState({ isSaving: false, open: false });
        },
      );
    } else {
      this.setState({ open: false });
    }
  };

  renderProviderPaper(provider) {
    const t = this.props.t;
    if (provider.id === null || provider.id === undefined) {
      provider.id = "__new_provider_id_for_" + provider.name; // Skip rendering if provider ID is not defined
    }
    /**
     * @type {Array<{id: string,name: string, logoUrl: string,disclaimer: string,active: boolean,showUnconfirmedFindings: boolean}>} Array of watermark providers, each provider should have the following properties:
     */
    return (
      <div key={"container__" + provider.id}>
        <div key={"wmp__" + provider.id} style={{ maxWidth: "825px" }}>
          <Paper
            onClick={(e) => this.toggleDetails(provider.id, e)}
            key={provider.id}
            className={this.props.classes.providerPaper}
            sx={{ boxShadow: "none" }}
          >
            <div className={this.props.classes.paperContainer}>
              <img
                src={provider.logoUrl}
                alt={provider.name}
                height="48px"
                className={this.props.classes.providerIcon}
              />
              <div className={this.props.classes.paperControls}>
                <div className={this.props.classes.providerSwitch}>
                  <Switch
                    checked={
                      this.state.activeStatusByProvider[provider.id] || provider.active
                    }
                    onChange={(e) => this.handleProviderToggle(provider.id, e)}
                    color="primary"
                  />
                </div>
                <div className={this.props.classes.providerStatus}>
                  <div
                    className={
                      provider.active
                        ? this.props.classes.providerStatusIndicatorActive
                        : this.props.classes.providerStatusIndicatorInactive
                    }
                  ></div>
                  <div className={this.props.classes.providerStatusText}>
                    {provider.active
                      ? this.props.t("_provider_active")
                      : this.props.t("_provider_inactive")}
                  </div>
                </div>
              </div>
            </div>
          </Paper>
          <Paper
            key={provider.id + "_details"}
            className={
              this.state.providerDetailsOpen === provider.id
                ? this.props.classes.providerDetailsPaperOpen
                : this.props.classes.providerDetailsPaperClosed
            }
          >
            <div style={{ padding: "15px" }}>
              <div className={this.props.classes.providerDetailsHeading}>
                API Key
              </div>
              <div className={this.props.classes.providerDetailsInput}>
                <TextField
                  value={
                    provider.id.startsWith("__new_provider_id_for_")
                      ? this.state.apiKeysByProvider[provider.id] || ""
                      : this.state.activeStatusByProvider[provider.id] === true || provider.active
                        ? "********"
                        : ""
                  }
                  fullWidth
                  key={provider.id + "_key_input"}
                  id={provider.id + "_key_input"}
                  label={t("_enter_api_key")}
                  className={this.props.classes.textField}
                  margin="dense"
                  type="password"
                  onChange={(e) => {
                    this.handleKeyChange(provider.id, e.target.value);
                  }}
                />
              </div>
              <div style={{ height: "10px" }}></div>
              <div className={this.props.classes.providerDetailsHeading}>
                {t("_apply_to_images")}
              </div>
              <div className={this.props.classes.providerDetailsInput}>
                <TagInput
                  tags={this.props.tagsReducer?.mediaTags}
                  value={this.state.tagsByProvider[provider.id] || provider.tags || []}
                  onTagsChanged={(action, value) =>
                    this.tagsChanged(provider.id, action, value)
                  }
                  isMulti={true}
                  isCreatable={false}
                />
                <div className={this.props.classes.selectOneTagWarning}>
                  {this.state.tagsByProvider[provider.id]?.length === 0
                    ? t("_select_one_tag_warning")
                    : ""}
                </div>
              </div>
              <div className={this.props.classes.providerFindingSetting}>
                <Switch
                  checked={
                    this.state.showUnconfirmedFindingsByProvider[provider.id] ||
                    provider.showUnconfirmedFindings ||
                    false
                  }
                  onChange={(e) => {
                    e.stopPropagation();
                    this.handleFindingsSettingChange(provider.id, e);
                  }}
                  color="primary"
                />
                <div style={{ marginLeft: "8px" }}>
                  {t("_show_unconfirmed_findings")}
                </div>
              </div>
              <div className={this.props.classes.providerDisclaimer}>
                {provider.disclaimer}
              </div>
              <div className={this.props.classes.providerDetailsActionButtons}>
                <span
                  style={
                    this.state.activeStatusByProvider[provider.id] || provider.active
                      ? { cursor: "pointer" }
                      : { cursor: "not-allowed", opacity: 0.5, color: "gray" }
                  }
                  onClick={() => this.saveChanges(provider)}
                >
                  {t("_update_configuration")}
                </span>
              </div>
            </div>
          </Paper>
          <div style={{ minHeight: "10px" }}></div>
        </div>
      </div>
    );
  }

  render() {
    const t = this.props.t;
    const providersettings = this.props.globalReducer.watermarkProviders;

    if (providersettings.length > 0) {
      return (
        <div className={this.props.classes.root}>
          {providersettings.map((provider) => {
            return this.renderProviderPaper(provider);
          })}

          <DialogPopup
            open={this.state.open}
            onClose={this.handleCloseResetPopup}
          >
            <DialogTitle>{t("_confirmation_required")}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {t("_deactivate_will_delete_watermarksettings")}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleCloseResetPopup} color="primary">
                {t("_cancel")}
              </Button>
              <Button onClick={this.actionClickedResetPopup} color="primary">
                {t("_yes")}
              </Button>
            </DialogActions>
          </DialogPopup>
        </div>
      );
    } else {
      return <>{t("no_watermark_providers")}</>;
    }
  }
}

function mapStateToProps(state) {
  return {
    globalReducer: state.globalReducer,
    tagsReducer: state.tagsReducer,
  };
}

function matchDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      actionShowSnackbar: showSnackbar,
      actionGetWatermarkProviders: getWatermarkProviders,
    },
    dispatch,
  );
}

export default withStyles(styles)(
  translate("translations")(
    connect(mapStateToProps, matchDispatchToProps)(WatermarkProvidersSettings),
  ),
);
